import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Award, Clock, CheckCircle2 } from "lucide-react";

export default function PortalVendorPerformance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/clinic/analytics/vendor-performance")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    // Mount-only fetch — api/toast/formatApiError are stable module imports
    // and setData is a stable setter. No closure-over-mutable-state risk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell
      eyebrow="Analytics · Vendor Performance"
      title="Operational visibility into vendor performance."
      intro="Scoring reflects operational performance, workflow reliability, and responsiveness only — not medical quality, clinical outcomes, or patient outcomes."
      testId="vendor-perf-page"
    >
      {!data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : data.vendors.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          No vendor activity yet. Submit a workflow request and metrics will populate as fulfillment progresses.
        </div>
      ) : (
        <>
          {/* Reliability bar chart */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Operational reliability score
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, data.vendors.length * 36)}>
              <BarChart data={data.vendors} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="vendor_name" tick={{ fontSize: 11 }} width={130} />
                <Tooltip />
                <Bar dataKey="reliability_score" name="Reliability" fill="#2563eb" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Scorecards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            {data.vendors.map((v) => (
              <ScoreCard v={v} key={v.vendor_id} />
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 overflow-x-auto">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Ranked vendor table
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Vendor</th>
                  <th className="py-2 px-3">Workflows</th>
                  <th className="py-2 px-3">Acceptance</th>
                  <th className="py-2 px-3">Completion</th>
                  <th className="py-2 px-3">Avg response</th>
                  <th className="py-2 px-3">Avg fulfillment</th>
                  <th className="py-2 px-3">Invoice turnaround</th>
                  <th className="py-2 px-3">Reliability</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.map((v) => (
                  <tr key={v.vendor_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`vendor-row-${v.vendor_id}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-900">
                      {v.vendor_name}
                      {v.preferred && (
                        <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[9px] uppercase font-bold">
                          <Award className="w-2.5 h-2.5" /> Preferred
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3">{v.total_workflows}</td>
                    <td className="py-2 px-3">{v.acceptance_rate}%</td>
                    <td className="py-2 px-3">{v.completion_rate}%</td>
                    <td className="py-2 px-3">{v.avg_response_hours == null ? "—" : `${v.avg_response_hours} h`}</td>
                    <td className="py-2 px-3">{v.avg_fulfillment_hours == null ? "—" : `${v.avg_fulfillment_hours} h`}</td>
                    <td className="py-2 px-3">{v.avg_invoice_turnaround_hours == null ? "—" : `${v.avg_invoice_turnaround_hours} h`}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${v.reliability_score >= 75 ? "bg-emerald-100 text-emerald-800" : v.reliability_score >= 50 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
                        {v.reliability_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5 text-[11px] text-slate-500 leading-relaxed">
            <b>Note:</b> Scoring uses operational signals only — response times, acceptance/completion rates, and invoice turnaround.
            It does not reflect medical quality, clinical outcomes, or patient outcomes.
          </div>
        </>
      )}
    </PageShell>
  );
}

function ScoreCard({ v }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-sm transition" data-testid={`vendor-card-${v.vendor_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-base font-bold text-slate-900">{v.vendor_name}</div>
          <div className="text-[10px] text-slate-500">{v.total_workflows} workflows tracked</div>
        </div>
        {v.preferred && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] uppercase font-bold">
            <Award className="w-2.5 h-2.5" /> Preferred
          </span>
        )}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <div className="font-display text-3xl font-extrabold text-slate-900">{v.reliability_score}</div>
        <div className="text-xs text-slate-500 mb-1">/ 100 reliability</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
        <Stat icon={CheckCircle2} label="Accept" v={`${v.acceptance_rate}%`} />
        <Stat icon={CheckCircle2} label="Complete" v={`${v.completion_rate}%`} />
        <Stat icon={Clock} label="Response" v={v.avg_response_hours == null ? "—" : `${v.avg_response_hours}h`} />
        <Stat icon={Clock} label="Invoice" v={v.avg_invoice_turnaround_hours == null ? "—" : `${v.avg_invoice_turnaround_hours}h`} />
      </div>
    </div>
  );
}
function Stat({ icon: Icon, label, v }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-50 rounded p-1.5">
      <Icon className="w-3 h-3 text-slate-400" />
      <span className="text-slate-500 text-[10px]">{label}</span>
      <span className="ml-auto font-bold text-slate-900">{v}</span>
    </div>
  );
}

void Legend;
